/**
 * Authentication Hook for React Components
 *
 * React 컴포넌트에서 JWT 인증을 쉽게 사용할 수 있는 커스텀 훅
 *
 * Features:
 * - 로그인/로그아웃 상태 관리
 * - 토큰 자동 갱신
 * - 사용자 정보 관리
 * - 로딩 상태 관리
 */

import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { TokenManager, TokenResponse } from '../utils/tokenManager';
import { authApi } from '../services/api';
import { User, LoginResponse } from '../types';
import SSOService, { AuthState as SSOAuthState } from '../services/ssoService';


export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  rememberMe: boolean;
  tokenInfo: {
    accessTokenExpiry: string;
    refreshTokenExpiry: string;
    timeUntilExpiry: string;
  } | null;
}

export interface AuthContextType extends AuthState {
  login: (user_id: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  checkTokenStatus: () => void;
  rememberMe: boolean;
}

// Auth Context 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 인증 상태를 관리하는 커스텀 훅
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * 인증 상태 관리를 위한 훅 구현
 */
export const useAuthState = () => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    rememberMe: TokenManager.getRememberMe(),
    tokenInfo: null,
  });

  const ssoService = SSOService.getInstance();

  /**
   * 토큰 상태 체크
   */
  const checkTokenStatus = useCallback(() => {
    const tokenInfo = TokenManager.getExpiryInfo();
    const isAuthenticated = TokenManager.isLoggedIn();
    const rememberMe = TokenManager.getRememberMe();

    setState(prevState => ({
      ...prevState,
      isAuthenticated,
      rememberMe,
      tokenInfo,
    }));
  }, []);

  /**
   * 사용자 정보 새로고침
   */
  const refreshUser = useCallback(async () => {
    try {
      if (!TokenManager.isLoggedIn()) {
        setState(prevState => ({
          ...prevState,
          isAuthenticated: false,
          user: null,
          isLoading: false,
          rememberMe: false,
        }));
        return;
      }

      setState(prevState => ({ ...prevState, isLoading: true }));

      const response = await authApi.me();
      const userData = response.data;

      setState(prevState => ({
        ...prevState,
        isAuthenticated: true,
        user: userData,
        isLoading: false,
      }));

      checkTokenStatus();

    } catch (error) {
      console.error('Failed to fetch user data:', error);

      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        rememberMe: false,
        tokenInfo: null,
      });

      // 토큰이 만료되었을 가능성이 있으므로 클리어
      TokenManager.clearTokens();
    }
  }, [checkTokenStatus]);

  /**
   * 로그인
   */
  const login = useCallback(async (user_id: string, password: string, rememberMe: boolean = false) => {
    try {
      setState(prevState => ({ ...prevState, isLoading: true }));

      console.log('🔐 Attempting login...', { rememberMe });
      const response = await authApi.login({ user_id, password, remember_me: rememberMe });
      const loginData = response.data as LoginResponse;
      
      // 토큰 저장
      TokenManager.saveTokens({
        access_token: loginData.access_token,
        token_type: loginData.token_type,
        expires_in: loginData.expires_in
      });

      // 토큰 저장 후 즉시 검증
      const tokenInfo = TokenManager.getTokenInfo();
      const isLoggedIn = TokenManager.isLoggedIn();
      console.log('🔑 Token saved and verified:', { 
        tokenInfo, 
        isLoggedIn,
        accessToken: TokenManager.getAccessToken() ? '있음' : '없음',
        refreshTokenCookie: TokenManager.getRefreshToken() ? '있음' : '없음'
      });

      if (!isLoggedIn) {
        throw new Error('토큰 저장 후 검증 실패');
      }

      console.log('✅ Login successful');

      // SSO 서비스에 로그인 상태 브로드캐스트
      ssoService.broadcastLogin(loginData.user, loginData.access_token);

      // 사용자 정보 새로고침
      await refreshUser();

    } catch (error) {
      console.error('❌ Login failed:', error);
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        rememberMe: false,
        tokenInfo: null,
      });
      throw error;
    }
  }, [refreshUser, ssoService]);

  /**
   * 로그아웃
   */
  const logout = useCallback(async () => {
    try {
      console.log('🚪 Logging out...');
      // 로그아웃 상태 설정하여 토큰 갱신 방지
      TokenManager.setLoggingOut(true);
      await authApi.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // SSO 서비스에 로그아웃 상태 브로드캐스트
      ssoService.broadcastLogout();

      TokenManager.clearTokens(); // 이 함수가 isLoggingOut을 false로 리셋함
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        rememberMe: false,
        tokenInfo: null,
      });
      console.log('✅ Logged out successfully');
    }
  }, [ssoService]);

  /**
   * SSO 서비스와 연동하여 인증 상태 동기화
   */
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔍 Initializing authentication with SSO...');

      // SSO 서비스에서 초기 인증 상태 가져오기
      const ssoAuthState = ssoService.getAuthState();

      if (ssoAuthState.isAuthenticated && ssoAuthState.user && ssoAuthState.accessToken) {
        // SSO에서 인증된 상태라면 TokenManager에 토큰 저장
        TokenManager.saveTokens({
          access_token: ssoAuthState.accessToken,
          token_type: "bearer",
          expires_in: 15 * 60 // 15분을 초로 변환
        });

        // 사용자 정보 업데이트
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: ssoAuthState.user,
          rememberMe: TokenManager.getRememberMe(),
          tokenInfo: TokenManager.getExpiryInfo(),
        });
      } else if (TokenManager.isLoggedIn()) {
        await refreshUser();
      } else {
        setState(prevState => ({ ...prevState, isLoading: false }));
      }
    };

    // SSO 인증 상태 변경 리스너 등록
    const handleSSOAuthChange = (ssoAuthState: SSOAuthState) => {
      console.log('🔄 SSO auth state changed:', ssoAuthState);

      if (ssoAuthState.isAuthenticated && ssoAuthState.user && ssoAuthState.accessToken) {
        // 로그인 상태로 변경
        TokenManager.saveTokens({
          access_token: ssoAuthState.accessToken,
          token_type: "bearer",
          expires_in: 15 * 60 // 15분을 초로 변환
        });

        setState({
          isAuthenticated: true,
          isLoading: false,
          user: ssoAuthState.user,
          rememberMe: TokenManager.getRememberMe(),
          tokenInfo: TokenManager.getExpiryInfo(),
        });
      } else {
        // 로그아웃 상태로 변경
        TokenManager.clearTokens();
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          rememberMe: false,
          tokenInfo: null,
        });
      }
    };

    ssoService.addAuthListener(handleSSOAuthChange);

    // 다른 탭에서 인증 상태 동기화 요청
    ssoService.requestSync();

    initializeAuth();

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      ssoService.removeAuthListener(handleSSOAuthChange);
    };
  }, [refreshUser, ssoService]);

  /**
   * 토큰 상태 주기적 체크 (1분마다)
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (state.isAuthenticated) {
        checkTokenStatus();
      }
    }, 60000); // 1분마다

    return () => clearInterval(interval);
  }, [state.isAuthenticated, checkTokenStatus]);

  return {
    ...state,
    login,
    logout,
    refreshUser,
    checkTokenStatus,
  };
};

/**
 * AuthProvider 컴포넌트
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authState = useAuthState();

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 로그인이 필요한 컴포넌트를 감싸는 HOC
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div>로딩 중...</div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // 로그인 페이지로 리다이렉트 또는 로그인 폼 표시
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
};

/**
 * 관리자 권한이 필요한 컴포넌트를 감싸는 HOC
 */
export const withAdminAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div>로딩 중...</div>
        </div>
      );
    }

    if (!isAuthenticated || !user) {
      window.location.href = '/login';
      return null;
    }

    if (user.role !== 'admin') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div>❌ 관리자 권한이 필요합니다.</div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

/**
 * 토큰 상태 모니터링을 위한 커스텀 훅
 */
export const useTokenMonitor = () => {
  const { tokenInfo, checkTokenStatus } = useAuth();
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    const updateTimeLeft = () => {
      if (tokenInfo?.timeUntilExpiry) {
        setTimeLeft(tokenInfo.timeUntilExpiry);
      } else {
        setTimeLeft('만료됨');
      }
    };

    // 즉시 업데이트
    updateTimeLeft();

    // 1초마다 업데이트
    const interval = setInterval(() => {
      checkTokenStatus();
      updateTimeLeft();
    }, 1000);

    return () => clearInterval(interval);
  }, [tokenInfo, checkTokenStatus]);

  return {
    tokenInfo,
    timeLeft,
    refreshToken: checkTokenStatus,
  };
};

export default useAuth;